// Semantic roles — which palette entry plays which part, per theme. Both themes
// `satisfies SemanticTokens`, so a role added to one and not the other is a type
// error, not a visual bug. Values come from `PALETTE` by name, never re-typed.

import { PALETTE } from './palette';

/** The role vocabulary. Adding a key obliges BOTH `DARK` and `LIGHT`. */
export interface SemanticTokens {
  /** The page. Never a gradient. */
  readonly 'bg-base': string;
  /** The one panel primitive's fill. */
  readonly 'bg-panel': string;
  /** A panel on a panel. */
  readonly 'bg-raised': string;
  readonly 'text-primary': string;
  readonly 'text-secondary': string;
  readonly 'text-muted': string;
  /** Disabled and decorative marks only — ~2:1, never text a reader needs. */
  readonly 'text-faint': string;
  /** Section rules. */
  readonly 'border-hairline': string;
  /** Panel borders. */
  readonly 'border-panel': string;
  /** Live / in-progress state ONLY — never decoration. */
  readonly accent: string;
  readonly 'accent-pressed': string;
  /** The accent's plate behind a live chip or row. */
  readonly 'accent-soft': string;
  readonly 'status-live': string;
  readonly 'status-shipped': string;
  /** The danger hue as a fill, dot or icon. */
  readonly 'status-danger': string;
  /** The danger hue as prose — kept apart so the fill may later leave AA-text
   *  range without every error line following it. Today both clear 4.5:1. */
  readonly 'status-danger-text': string;
  readonly 'status-warning': string;
  /** The logo's body; its single lit face is `accent`. */
  readonly 'logo-fill': string;
}

/** `#rrggbb` at `alpha`, as `rgba(…)`. */
function withAlpha(hex: string, alpha: number): string {
  const channel = (at: number): number => Number.parseInt(hex.slice(at, at + 2), 16);
  return `rgba(${channel(1)}, ${channel(3)}, ${channel(5)}, ${alpha})`;
}

export const DARK = {
  'bg-base': PALETTE.void,
  'bg-panel': PALETTE.panel,
  'bg-raised': PALETTE.panelRaised,
  'text-primary': PALETTE.ink,
  'text-secondary': PALETTE.ink2,
  'text-muted': PALETTE.ink3,
  'text-faint': PALETTE.ink4,
  'border-hairline': PALETTE.hairlineRule,
  'border-panel': PALETTE.hairlinePanel,
  accent: PALETTE.working,
  'accent-pressed': PALETTE.workingDeep,
  // The canvas's live-row plate: working at .13.
  'accent-soft': withAlpha(PALETTE.working, 0.13),
  'status-live': PALETTE.working,
  'status-shipped': PALETTE.shipped,
  'status-danger': PALETTE.danger,
  'status-danger-text': PALETTE.danger,
  'status-warning': PALETTE.warning,
  'logo-fill': PALETTE.ink,
} as const satisfies SemanticTokens;

export const LIGHT = {
  'bg-base': PALETTE.paper,
  'bg-panel': PALETTE.paperPanel,
  'bg-raised': PALETTE.paperRaised,
  'text-primary': PALETTE.inkLight,
  'text-secondary': PALETTE.inkLight2,
  'text-muted': PALETTE.inkLight3,
  'text-faint': PALETTE.inkLight4,
  'border-hairline': PALETTE.hairlineLightRule,
  'border-panel': PALETTE.hairlineLightPanel,
  accent: PALETTE.workingLight,
  // Pressed goes DARKER than the accent on paper; `workingDeep` is lighter than
  // `workingLight`, so the darker indigo the light theme already carries plays it.
  'accent-pressed': PALETTE.linkLight,
  'accent-soft': withAlpha(PALETTE.workingLight, 0.1),
  'status-live': PALETTE.workingLight,
  'status-shipped': PALETTE.shippedLight,
  'status-danger': PALETTE.dangerLight,
  'status-danger-text': PALETTE.dangerLight,
  'status-warning': PALETTE.warningLight,
  'logo-fill': PALETTE.inkLight,
} as const satisfies SemanticTokens;
